// src/admin/pages/SettingsPage.jsx
import React, { useState, useEffect } from "react";
import { adminGetSettings, adminDeleteSettings } from "../api/admin";
import SettingsTable from "../components/SettingsTable";
import SettingsForm from "../components/SettingsForm";
import "./SettingsPage.css";
import { toast } from "react-toastify";

const SettingsPage = () => {
  const [settingsList, setSettingsList] = useState([]);
  const [editingSettings, setEditingSettings] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  // RBAC check
  const hasWritePermission = () => {
    const role = localStorage.getItem("adminRole");
    if (role === "admin") return true;
    const permissions = JSON.parse(localStorage.getItem("adminPermissions") || "[]");
    return permissions.includes("Settings:write");
  };
  const canWrite = hasWritePermission();

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await adminGetSettings();
      // The API might return a single object or an array. 
      // Wrapping in array for the table component.
      const data = res.data;
      if (data) {
        setSettingsList(Array.isArray(data) ? data : [data]);
      } else {
        setSettingsList([]);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      // If 404, it might just mean no settings created yet
      if (error.response?.status !== 404) {
        toast.error("Failed to load settings.");
      }
      setSettingsList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);


  const handleCreateNew = () => {
    if (settingsList.length > 0) {
      toast.warning("Only one settings document is allowed. Please edit the existing one.");
      return;
    }
    setEditingSettings(null);
    setShowForm(true);
  };

  const handleEdit = (settings) => {
    setEditingSettings(settings);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete the site settings? This may affect site functionality.")) return;
    try {
      await adminDeleteSettings(id);
      toast.success("Settings deleted successfully! 🗑️");
      fetchSettings();
    } catch (error) {
      console.error("Error deleting settings:", error);
      toast.error(error.response?.data?.message || "Failed to delete settings.");
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingSettings(null);
  };

  return (
    <div className="settings-page">
      {!showForm ? (
        // TABLE VIEW
        <>
          <div className="page-header">
            <h2 className="admin-title">Site Settings</h2>
            <div className="header-actions">
              {settingsList.length === 0 && !loading && canWrite && (
                <button className="btn-add-settings" onClick={handleCreateNew}>
                  + Add Settings
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="loading-container">Loading settings...</div>
          ) : (
            <SettingsTable
              settingsList={settingsList}
              onEdit={handleEdit}
              onDelete={handleDelete}
              canWrite={canWrite}
            />
          )}
        </>
      ) : (
        // FORM VIEW (Replacing table)
        <>
          <div className="page-header">
            <div className="header-with-back">
              <button className="back-btn" onClick={closeForm}>← Back</button>
              <h2 className="admin-title">
                {editingSettings ? "Update" : "Create"} Site Settings
              </h2>
            </div>
          </div>
          <div className="settings-form-inline-container">
            <SettingsForm
              settings={editingSettings}
              onSuccess={() => {
                fetchSettings();
                closeForm();
              }}
              onCancel={closeForm}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default SettingsPage;

